import { ChevronDown } from "lucide-react";
import { BUCKETS, dueText, deliveryText, projectLabel, todayISO } from "@/lib/orders";
import { formatCZKCompact, plur } from "@/lib/format";
import { StatusPill, SupplierMark, ModeIcon, Warn, Amount, orderAmount } from "./OrderBits";

const GRID = "grid grid-cols-[minmax(0,1fr)_minmax(96px,auto)_minmax(112px,auto)] lg:grid-cols-[minmax(170px,1.2fr)_minmax(150px,1.3fr)_minmax(110px,.8fr)_minmax(150px,1fr)_minmax(110px,.75fr)_minmax(100px,.7fr)_118px] gap-3 items-center";

// Tablet (md): dodavatel s tím, co a kam, v jednom sloupci; PC (lg): sedm sloupců jako v Excelu.
function Row({ o, today, money, onOpen, am }) {
  const dueTone = o.late ? "text-destructive font-semibold" : (o.due_date === today && o.status !== "received") ? "pp-ink font-bold" : "font-medium";
  const pas = (o.lines || []).filter((l) => l.pasport_item_id).length;
  const paidNote = o.status === "received" && <span className={`text-[11.5px] font-semibold ${o.paid ? "text-success" : "text-muted-foreground"}`}>{o.paid ? "zaplaceno" : "nezaplaceno"}</span>;
  return (
    <button type="button" onClick={() => onOpen(o)} data-testid={`order-row-${o.id}`}
      className={`${GRID} w-full text-left px-4 py-3 border-t border-border/70 first:border-t-0 hover:bg-secondary/50 transition-colors motion-reduce:transition-none`}>
      <span className="flex items-center gap-2.5 min-w-0">
        <SupplierMark name={o.supplier_name} />
        <span className="min-w-0">
          <b className="block text-[14.5px] font-bold truncate">{o.supplier_name}<span className="lg:hidden font-semibold"> · {o.title}</span></b>
          <span className="hidden lg:block text-[12.5px] text-muted-foreground truncate">{o.order_no ? `č. ${o.order_no}` : "bez čísla"}</span>
          <span className="lg:hidden flex items-center gap-1 text-[12.5px] text-muted-foreground min-w-0">
            <ModeIcon o={o} size={13} /><span className="truncate">{projectLabel(o)} · {deliveryText(o)}</span>
          </span>
        </span>
      </span>
      <span className="hidden lg:block min-w-0">
        <span className="block text-[14px] font-semibold truncate">{o.title}</span>
        {pas > 0 && <span className="block text-[12px] text-muted-foreground truncate">z pasportu · {pas} {pas === 1 ? "položka" : pas <= 4 ? "položky" : "položek"}</span>}
      </span>
      <span className="hidden lg:block text-[13.5px] truncate">{projectLabel(o)}</span>
      <span className="hidden lg:flex items-center gap-1.5 text-[13px] text-muted-foreground min-w-0"><ModeIcon o={o} /><span className="truncate">{deliveryText(o)}</span></span>
      <span className="min-w-0">
        <span className={`block text-[13.5px] whitespace-nowrap ${dueTone}`}>{dueText(o, today)}</span>
        <Warn o={o} today={today} className="block truncate" />
      </span>
      <span className="hidden lg:block text-right">{money && <Amount o={o} am={am} className="text-[14px] font-bold" />}</span>
      <span className="flex flex-col items-end lg:items-start gap-0.5 min-w-0">
        {money && <Amount o={o} am={am} className="lg:hidden text-[13.5px] font-bold" />}
        <StatusPill o={o} />
        {paidNote}
      </span>
    </button>
  );
}

const HEAD = { net: "Bez DPH", gross: "Částka s DPH", both: "Bez / s DPH" };
function Head({ money, am }) {
  return (
    <div className={`${GRID} !hidden lg:!grid px-4 py-2.5 text-[11px] font-bold uppercase tracking-widest text-muted-foreground`}>
      <span>Dodavatel</span><span>Co</span><span>Zakázka</span><span>Doručení</span><span>Termín</span>
      <span className="text-right">{money ? HEAD[am] : ""}</span><span>Stav</span>
    </div>
  );
}
const NOTE = Object.fromEntries(BUCKETS.map((b) => [b.key, b.note]));

// Seznam (PC): 391 — skupiny a pořadí podle Zobrazení (výchozí Co dělat · Termín, jako dřív). Skupina se sbalí klikem
// na lištu; vpravo počet a součet podle Zobrazení › Částky. Bez skupin jeden seznam.
export function OrderList({ rows, groups, closed, onToggle, groupBy, status, money, onOpen, am = "gross" }) {
  const today = todayISO();
  if (!rows.length) {
    return (
      <div className="glass rounded-[20px] p-10 text-center text-muted-foreground" data-testid="orders-empty">
        {status === "done" ? "Zatím nic vyřízeného — objednávka sem dojde sama, až je převzatá i zaplacená."
          : status === "cancelled" ? "Žádná zrušená objednávka." : "Žádná rozpracovaná objednávka. Novou založíte tlačítkem nahoře."}
      </div>
    );
  }
  const list = (items) => <div className="bg-card/70">{items.map((o) => <Row key={o.id} o={o} today={today} money={money} am={am} onOpen={onOpen} />)}</div>;
  if (!groups) {
    return (
      <section className="glass rounded-[20px] overflow-hidden" data-testid={`orders-flat-${status}`}>
        <Head money={money} am={am} />{list(rows)}
      </section>
    );
  }
  const base = am === "both" ? "net" : am;
  return (
    <div className="space-y-4" data-testid="orders-list">
      {groups.map((g) => {
        const shut = closed?.has(g.key);
        const sum = money ? g.rows.reduce((t, o) => t + (orderAmount(o, base) || 0), 0) : 0;
        const red = groupBy === "bucket" && g.key === "now";
        return (
          <section key={g.key || "none"} className="glass rounded-[20px] overflow-hidden" data-testid={`orders-group-${g.key || "none"}`}>
            <button type="button" onClick={() => onToggle(g.key)} aria-expanded={!shut}
              className="w-full flex items-baseline gap-2.5 flex-wrap px-4 pt-3.5 pb-1 text-left">
              <ChevronDown size={16} className={`self-center shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none ${shut ? "-rotate-90" : ""}`} />
              <h2 className={`text-[15px] font-extrabold ${red ? "text-destructive" : ""}`}>{g.label}</h2>
              <span className="text-[13px] font-bold tabular-nums text-muted-foreground">{g.rows.length}</span>
              {groupBy === "bucket" && NOTE[g.key] && <span className="text-[12.5px] text-muted-foreground">· {NOTE[g.key]}</span>}
              {money && sum > 0 && <span className="ml-auto text-[13px] font-semibold text-muted-foreground tabular-nums whitespace-nowrap" title={plur(g.rows.length, "objednávka", "objednávky", "objednávek")}>
                {formatCZKCompact(sum)} {base === "gross" ? "s DPH" : "bez DPH"}</span>}
            </button>
            {shut ? <div className="h-2" /> : <><Head money={money} am={am} />{list(g.rows)}</>}
          </section>
        );
      })}
    </div>
  );
}
